/**
 * WorkspaceToolGrid
 *
 * Drop-anywhere 3-tool recommendation strip for marketing / public pages.
 * Each tool card is an auth-aware WorkspaceLink so signed-out users land
 * in the auth modal (lead capture) and are redirected to the tool after
 * sign-up.
 *
 * Usage:
 *   <WorkspaceToolGrid
 *     tools={['cost-tracker', 'furniture-planner', 'quote-comparison']}
 *     eyebrow="Try this in your CoPilot"
 *     heading="3 tools that pair with this package"
 *     testIdPrefix="package-tool-grid"
 *   />
 *
 * Props
 *   tools          array of 1-3 tool ids from TOOL_REGISTRY below
 *   eyebrow        small uppercase label (defaults to "Try this in your CoPilot")
 *   heading        h3-style title above the cards
 *   contextLabel   optional extra context passed via ?from= query param
 *   variant        'sidebar' (compact column) | 'row' (default 3-card row)
 *   testIdPrefix   namespace for data-testids
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import WorkspaceLink from './WorkspaceLink';
import { TOOL_ICONS } from '../workspace/lib/toolIcons';

// ─── Central tool registry ─────────────────────────────────────────────
// Adding a tool here makes it usable on any public page via the `tools`
// prop. Keep blurbs short (≤14 words) - they sit in a card.
//
// We intentionally do NOT include `interior-3d` here - product decision
// 2026-02 by user, that tool isn't part of the current recommendation
// surface.
export const TOOL_REGISTRY = {
  'cost-tracker': {
    icon: TOOL_ICONS['cost-tracker'],
    title: 'Cost Tracker',
    blurb: 'Track every payment, deposit and variation against your build budget.',
    to: '/workspace/tools/cost-tracker',
  },
  'furniture-planner': {
    icon: TOOL_ICONS['furniture-planner'],
    title: 'Furniture Planner',
    blurb: 'Drop your existing furniture into a floor plan to test how it fits.',
    to: '/workspace/tools/furniture-planner',
  },
  'quote-comparison': {
    icon: TOOL_ICONS['quote-compare'],
    title: 'Quote Comparison',
    blurb: 'Upload builder quotes and get them normalised side-by-side in 60 seconds.',
    to: '/workspace/tools/quote-comparison',
  },
  'find-builder': {
    icon: TOOL_ICONS['find-builder'],
    title: 'Find a Builder',
    blurb: 'Get matched to suitable builders based on your block, budget and style.',
    to: '/workspace/tools/find-a-builder',
  },
  'solar-orientation': {
    icon: TOOL_ICONS['solar'],
    title: 'Solar Orientation',
    blurb: 'See how the sun moves across this block and where to put living rooms.',
    to: '/workspace/tools/solar-orientation',
  },
  'defects-scanner': {
    icon: TOOL_ICONS['defects'],
    title: 'Defects Scanner',
    blurb: 'Walk a display home or your own build and snap photos - we classify issues.',
    to: '/workspace/tools/defects-scanner',
  },
  'checklists': {
    icon: TOOL_ICONS['checklists'],
    title: 'Stage Checklists',
    blurb: 'Take a stage-by-stage checklist with you across every build phase.',
    to: '/workspace/tools/checklists',
  },
  'lifestyle-discovery': {
    icon: TOOL_ICONS['fhb-discovery'],
    title: 'Lifestyle Discovery',
    blurb: '60-second quiz that matches you to suburbs and home types that fit how you live.',
    to: '/workspace/tools/lifestyle-discovery',
  },
  'house-and-land': {
    icon: TOOL_ICONS['house-and-land'],
    title: 'H&L Matchmaker',
    blurb: 'Tell us your block, budget and bedrooms - we surface matching packages.',
    to: '/workspace/tools/house-and-land-matchmaker',
  },
  'project-health': {
    icon: TOOL_ICONS['project-health'],
    title: 'Project Health',
    blurb: 'Live read-out of where your build stands - risks, deadlines, gaps.',
    to: '/workspace/tools/project-health',
  },
  'documents': {
    icon: TOOL_ICONS['documents'],
    title: 'Documents',
    blurb: 'Store contracts, variations and quotes in one secure place.',
    to: '/workspace/tools/documents',
  },
  'facade-visualiser': {
    icon: TOOL_ICONS['facade'],
    title: 'Facade Visualiser',
    blurb: 'Try different facade looks against the same floor plan.',
    to: '/workspace/tools/facade-visualiser',
  },
  'milestones': {
    icon: TOOL_ICONS['milestones'],
    title: 'Milestones',
    blurb: 'Plot the dates that actually matter - deposits, slab, frame, handover.',
    to: '/workspace/tools/milestones',
  },
  'contacts': {
    icon: TOOL_ICONS['contacts'],
    title: 'Contacts Hub',
    blurb: 'Keep every builder, broker and trade in one tidy address book.',
    to: '/workspace/tools/contacts-hub',
  },
};

const WorkspaceToolGrid = ({
  tools = [],
  eyebrow = 'Useful tools',
  heading,
  contextLabel,
  variant = 'row',
  testIdPrefix = 'tool-grid',
  className = '',
}) => {
  const items = tools
    .map((id) => ({ id, ...TOOL_REGISTRY[id] }))
    .filter((t) => t.title);

  if (items.length === 0) return null;

  const isSidebar = variant === 'sidebar';
  const wrapClass = isSidebar
    ? 'flex flex-col gap-3'
    : 'grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4';

  const buildHref = (to) => (contextLabel
    ? `${to}?from=${encodeURIComponent(contextLabel)}`
    : to);

  return (
    <section
      className={`my-10 ${className}`}
      data-testid={`${testIdPrefix}-wrap`}
      aria-label={heading || eyebrow}
    >
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-[0.04em] font-semibold text-[#1d8efe]">
          {eyebrow}
        </p>
        {heading && (
          <h3 className="text-[20px] sm:text-[22px] font-bold text-[#1d1d1f] mt-1 leading-tight">
            {heading}
          </h3>
        )}
      </div>
      <div className={wrapClass}>
        {items.map((tool) => (
          <WorkspaceLink
            key={tool.id}
            to={buildHref(tool.to)}
            className="group flex flex-col items-start gap-3 p-4 sm:p-5 rounded-2xl bg-white border border-[#e8e8ed] hover:border-[#1d8efe]/40 hover:shadow-[0_8px_24px_rgba(29,142,254,0.10)] transition-all no-underline"
            testId={`${testIdPrefix}-${tool.id}`}
          >
            <span className="w-12 h-12 rounded-xl bg-[#f5f7fb] flex items-center justify-center overflow-hidden">
              <img
                src={tool.icon}
                alt=""
                aria-hidden="true"
                className="w-9 h-9 object-contain"
                loading="lazy"
                draggable={false}
              />
            </span>
            <div className="flex-1">
              <h4 className="text-[15px] font-semibold text-[#1d1d1f] leading-tight">
                {tool.title}
              </h4>
              <p className="text-[13px] text-[#5a5a5f] mt-1 leading-snug">
                {tool.blurb}
              </p>
            </div>
            <span className="text-[12px] font-semibold text-[#1d8efe] inline-flex items-center gap-1 mt-1 group-hover:gap-1.5 transition-all">
              Open <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </WorkspaceLink>
        ))}
      </div>
    </section>
  );
};

export default WorkspaceToolGrid;
